<div class="participant-list-container">
  <div class="header">
    <h2 class="font-semibold">{{ 'VideoCall.Participants.Title' | translate }} ({{ roster().length }})</h2>
    <button mat-icon-button btnType="action-icon" (click)="closePanel.emit()" [matTooltip]="'Global.Close' | translate">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Host Global Controls -->
  @if (isHost()) {
    <div class="flex items-center justify-between px-3 py-2 border-b border-[var(--skz-border-color-secondary)] bg-[var(--skz-background-color-light)]">
      <div class="flex items-center gap-1">
        <button
          mat-icon-button
          btnType="action-icon"
          (click)="toggleLockAudio.emit()"
          [matTooltip]="(isAudioLocked() ? 'VideoCall.Participants.UnlockAudio' : 'VideoCall.Participants.LockAudio') | translate"
        >
          <mat-icon class="text-[15px]">{{ isAudioLocked() ? 'mic_off' : 'mic' }}</mat-icon>
        </button>
        <button
          mat-icon-button
          btnType="action-icon"
          (click)="toggleLockCamera.emit()"
          [matTooltip]="(isCameraLocked() ? 'VideoCall.Participants.UnlockCamera' : 'VideoCall.Participants.LockCamera') | translate"
        >
          <mat-icon class="text-[15px]">{{ isCameraLocked() ? 'videocam_off' : 'videocam' }}</mat-icon>
        </button>
        <button
          mat-icon-button
          btnType="action-icon"
          (click)="toggleLockScreenShare.emit()"
          [matTooltip]="(isScreenShareLocked() ? 'VideoCall.Participants.UnlockScreenShare' : 'VideoCall.Participants.LockScreenShare') | translate"
        >
          <mat-icon class="text-[15px]">{{ isScreenShareLocked() ? 'stop_screen_share' : 'screen_share' }}</mat-icon>
        </button>
        <button
          mat-icon-button
          btnType="action-icon"
          (click)="toggleLockChat.emit()"
          [matTooltip]="(isChatLocked() ? 'VideoCall.Participants.UnlockChat' : 'VideoCall.Participants.LockChat') | translate"
        >
          <mat-icon class="text-[15px]">{{ isChatLocked() ? 'chat_bubble_off' : 'chat_bubble' }}</mat-icon>
        </button>
      </div>

      @if (roster().length > 1) {
        <button mat-icon-button btnType="action-icon" (click)="muteAll.emit()" [matTooltip]="'VideoCall.Participants.MuteAll' | translate">
          <mat-icon class="text-[15px]">volume_off</mat-icon>
        </button>
      }
    </div>
  }

  <div class="participant-list-body custom-scrollbar">
    <!-- Waiting Room Section -->
    @if (isHost() && waitingParticipants().length > 0) {
      <div class="px-3 pt-3">
        <app-waiting-room-list [participants]="waitingParticipants()" [collapsible]="true" (admit)="admitParticipant.emit($event)" (reject)="rejectParticipant.emit($event)" />
      </div>
    }

    <!-- Active Participants -->
    @if (waitingParticipants().length > 0 && roster().length > 0) {
      <div class="px-3 pt-3 pb-1">
        <span class="text-[12px] font-semibold tracking-wider text-[var(--skz-text-muted)]">{{ 'VideoCall.Participants.InMeeting' | translate }} ({{ roster().length }})</span>
      </div>
    }

    @for (p of roster(); track p.attendee.chimeAttendeeId) {
      <div class="participant-item group">
        <!-- Avatar -->
        <div class="participant-avatar">
          <mat-icon class="text-[14px] text-[var(--skz-text-muted)]">person</mat-icon>
        </div>

        <!-- Name + badges -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-1.5 flex-wrap">
            <span class="text-[13px] font-medium text-[var(--skz-text-primary)] truncate">
              {{ p.isLocal ? p.attendee.username + ' (' + ('You' | translate) + ')' : p.attendee.username }}
            </span>

            @if (hostAttendeeIds().has(p.attendee.chimeAttendeeId)) {
              <span class="host-badge">{{ 'VideoCall.Participants.HostLabel' | translate }}</span>
            }
          </div>
        </div>

        <!-- Status icons -->
        <div class="flex items-center gap-1">
          @if (p.attendee.isMuted) {
            <mat-icon class="status-icon muted" title="{{ 'VideoCall.Participants.Muted' | translate }}">mic_off</mat-icon>
          } @else {
            <mat-icon
              [class.speaking-indicator]="p.attendee.chimeAttendeeId === activeSpeakerId()"
              [class.active-mic]="p.attendee.chimeAttendeeId !== activeSpeakerId()"
              class="status-icon"
              title="{{ 'VideoCall.Participants.ActiveMic' | translate }}"
            >
              mic
            </mat-icon>
          }

          @if (p.tileState) {
            <mat-icon class="status-icon cam-on" title="{{ 'VideoCall.Participants.CameraOn' | translate }}">videocam</mat-icon>
          } @else {
            <mat-icon class="status-icon muted" title="{{ 'VideoCall.Participants.CameraOff' | translate }}">videocam_off</mat-icon>
          }
        </div>

        <!-- Moderator actions (hover-reveal) -->
        @if (isHost() && !p.isLocal && !hostAttendeeIds().has(p.attendee.chimeAttendeeId)) {
          <div class="moderator-actions">
            @if (!p.attendee.isMuted) {
              <button mat-icon-button btnType="action-icon" (click)="muteParticipant.emit(p.attendee.chimeAttendeeId)" [matTooltip]="'VideoCall.Participants.Mute' | translate">
                <mat-icon class="text-[15px]">volume_off</mat-icon>
              </button>
            }
            @if (p.tileState) {
              <button mat-icon-button btnType="action-icon" (click)="stopVideoParticipant.emit(p.attendee.chimeAttendeeId)" [matTooltip]="'VideoCall.Participants.StopVideo' | translate">
                <mat-icon class="text-[15px]">videocam_off</mat-icon>
              </button>
            }
            <button mat-icon-button btnType="action-icon" (click)="onPromoteClick($event, p.attendee.chimeAttendeeId)" [matTooltip]="'VideoCall.Participants.Promote' | translate">
              <mat-icon class="text-[15px]">admin_panel_settings</mat-icon>
            </button>
            <button mat-icon-button btnType="action-icon" (click)="onKickClick($event, p.attendee.chimeAttendeeId)" [matTooltip]="'VideoCall.Participants.Kick' | translate">
              <mat-icon class="text-[15px]">person_remove</mat-icon>
            </button>
          </div>
        }
      </div>
    }
  </div>
</div>
